<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, type PropType } from 'vue';
import { DateMode, type FormField } from '@/models/form';
import DatePicker from 'primevue/datepicker';
import 'primeicons/primeicons.css';
import { CustomButton, ButtonType, resetCalculatedValue } from '@/components';
import { getLocalTimezoneDetails, jsToPrimeVueDateFormat, shiftTimestampByDateShift } from '@/utils';
import { useEntryStore } from '@/stores/entry';
import { useI18n } from 'vue-i18n';
import type { CaseModel } from '@/models';
import { DateTime } from 'luxon';

const { t: $t } = useI18n();

const props = defineProps({
  field: {
    type: Object as PropType<FormField>,
    required: true
  },
  previewMode: {
    type: Boolean
  },
  selectedCase: {
    type: Object as PropType<CaseModel>
  }
});

const store = useEntryStore();
const localField = ref<FormField>(props.field);
const originalDate = ref<Date>();

const maxDate = ref();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let intervalId: any = null;
onMounted(() => {
  if (localField.value.options.dateConstraints?.max == 'now') {
    maxDate.value = new Date();
    intervalId = setInterval(() => {
      maxDate.value = new Date();
    }, 60000);
  }
});

onUnmounted(() => {
  clearInterval(intervalId);
});

const resetDate = () => {
  localField.value.dateValue = undefined;
  originalDate.value = undefined;
  localField.value.timezoneRecorded = undefined;
  localField.value.overrideCalculatedValue = undefined;
  if (store.form?.items) {
    resetCalculatedValue(localField.value, store.form?.items);
  }
};

const onInputChanges = () => {
  localField.value.inputChanged = true;
  localField.value.timezoneRecorded = getLocalTimezoneDetails();
  if (localField.value.options.overrideCalculation) {
    localField.value.overrideCalculatedValue = true;
    localField.value.timezoneRecorded = {...localField.value.timezoneRecorded, overrideCalculatedValue: true};
  }

  localField.value.dateValue?.setSeconds(0);
  localField.value.dateValue?.setMilliseconds(0);
};

const dateFormat = computed(() => {
  if (!props.previewMode) {
    if (localField.value.options.mode === DateMode.DATE_TIME && localField.value.options.format) {
      if (localField.value.options.timeFormat == 24) {
        return !localField.value.options.format.toLowerCase().includes('hh:mm') ? `${localField.value.options.format} HH:mm` : '';
      }
      return !localField.value.options.format.toLowerCase().includes('hh:mm') ? `${localField.value.options.format} hh:mm a` : '';
    } else if (localField.value.options.mode === DateMode.DATE && localField.value.options.format) {
      return localField.value.options.format;
    }
  }
  return '';
});

const setCurrentDate = () => {
  localField.value.inputChanged = true;
  localField.value.timezoneRecorded = getLocalTimezoneDetails();
  if (localField.value.options.overrideCalculation) {
    localField.value.overrideCalculatedValue = true;
    localField.value.timezoneRecorded = {...localField.value.timezoneRecorded, overrideCalculatedValue: true};
  }
  if (localField.value.options.mode === DateMode.DATE) {
    localField.value.dateValue = DateTime.now().startOf('day').toJSDate();
  } else {
    localField.value.dateValue = DateTime.now().startOf('minute').toJSDate();
  }
};

const showOriginalDate = () => {
  if (props.selectedCase?.shiftKey) {
    if (!originalDate.value) {
      const shiftdDate = shiftTimestampByDateShift(DateTime.fromJSDate(localField.value.dateValue!).toSeconds(), props.selectedCase?.shiftKey);
      originalDate.value = DateTime.fromSeconds(shiftdDate).toJSDate();
    } else {
      originalDate.value = undefined;
    }
  }
};

const selectedDate = computed({
  get: () => originalDate.value ?? localField.value.dateValue,
  set: val => {
    localField.value.dateValue = val;
  }
});
</script>

<template>
    <div class="item-root">
      <div class="item-picker">
        <DatePicker v-model="selectedDate"
          class="date-picker"
          :timeOnly="localField.options.mode === DateMode.TIME"
          :showTime="localField.options.mode === DateMode.DATE_TIME"
          showIcon
          @dateSelect="onInputChanges"
          @change="onInputChanges"
          :dateFormat="jsToPrimeVueDateFormat(localField.options.format)"
          :placeholder="dateFormat"
          :hour-format="localField.options.timeFormat == 12 ? '12' : '24'"
          :disabled="previewMode || originalDate != undefined"
          :maxDate="maxDate"
          iconDisplay="input"
          fluid >
          <template #inputicon="slotProps">
            <i :class="`pi ${
                localField.options.mode === DateMode.TIME ? 'pi-clock' :
                localField.options.mode === DateMode.DATE_TIME ? 'pi-calendar-clock': 'pi-calendar'}`
              "
              @click="slotProps.clickCallback" />
          </template>
        </DatePicker>

        <CustomButton :text="!originalDate ?  'Show shifted' : 'Show original'"
          @onClick="showOriginalDate"
          class="reset-button"
          :marginLeft="16"
          v-if="!previewMode && localField.options.dateShifting && localField.dateValue && selectedCase?.shiftKey"
          :type="ButtonType.OUTLINED"/>

        <CustomButton :text="localField.options.mode === DateMode.DATE ? $t('today') : $t('now')"
          @onClick="setCurrentDate"
          class="reset-button"
          :marginLeft="16"
          v-if="!previewMode"
          :type="ButtonType.OUTLINED"/>

        <CustomButton :text="$t('reset')"
          @onClick="resetDate"
          class="reset-button"
          :marginLeft="16"
          v-if="!previewMode && localField.dateValue"
          :type="ButtonType.OUTLINED"/>
      </div>
      <div class="shifted-date" v-if="localField.options.dateShifting && localField.dateValue">
        This date is shifted for added security.
      </div>
      <div class="timezone-info"
        v-if="localField.options.absoluteDate && localField.timezoneRecorded">
        This date is recorded in timezone: {{ localField.timezoneRecorded.timezone }}
      </div>
    </div>
</template>

<style scoped lang="scss">
.item-picker {
  display: flex;
  align-items: center;
}
.date-picker {
  min-width: 300px;
}
.timezone-info {
  margin-top: 8px;
  font-style: italic;
}

@media (max-width: 600px) {
  .item-picker {
    flex-direction: column;
    align-items: start;
    gap: 8px;
    .reset-button {
      margin: 0;
    }
  }
}
</style>
